import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button, IconButton, Switch, Tooltip } from "@mui/material";
import { Add, InfoOutlined } from "@mui/icons-material";
import AppTable from "components/Shared/AppTable";
import {
  useLazyGetRegionListQuery,
  useUpdateRegionStatusMutation,
} from "store/services/LocationsApi/regionApi";
import TableFilter from "components/Shared/AppTable/TableFilter";
import { useAppMeta, useAuth, useTablePagination } from "hooks";
import HighlightedText from "components/Shared/HighlightedText";
import moment from "moment";
import CustomTabs from "components/Shared/CustomTabs";
import { toast } from "react-toastify";

const RegionListScreen = () => {
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();

  const [searchParams] = useSearchParams();
  const keyword = searchParams.get("search") || "";

  const { isSystemAdmin } = useAuth();

  const breadcrumbs = [{ title: "Province" }];

  const activeTab = searchParams.get("tab") || "Active";

  const [updateRegionStatus] = useUpdateRegionStatusMutation();

  const regionListTabs = [
    { label: "Active", value: "Active" },
    { label: "Inactive", value: "Inactive" },
  ];
  // status update
  const handleStatusChange = (row) => {
    const newStatus = row.status === "Active" ? "Inactive" : "Active";

    const isConfirmed = window.confirm(
      `Are you sure you want to change the status to ${newStatus}?`
    );

    if (isConfirmed) {
      updateRegionStatus({ id: row._id, status: newStatus })
        .unwrap()
        .then(() => {
          toast.success(`Province status updated to ${newStatus}`);
        })
        .catch((error) => {
          const errorMessage = error?.data?.message || "Something went wrong!";
          toast.error(errorMessage);
        });
    }
  };

  const [
    fetchRegions,
    {
      data: regionRes,
      isLoading: loadingRegions,
      isFetching: fetchingRegions,
      error,
    },
  ] = useLazyGetRegionListQuery();

  const columns = [
    {
      name: "Name",
      selector: (row) => row?.name,
      cell: (row) =>
        row?.name ? (
          <HighlightedText text={row?.name} highlight={keyword} />
        ) : (
          "N/A"
        ),
      sortable: true,
    },
    {
      name: "Code",
      selector: (row) => row?.code,
      sortable: true,
    },

    {
      name: "CreatedAt",
      selector: (row) => moment(row?.createdAt).format("YYYY-MM-DD"),
      sortable: true,
    },
    {
      name: "Actions",
      cell: (row) => (
        <>
          <Link to={`/app/crm/region/${row._id}/detail`}>
            <Tooltip title={"View Detail"} arrow>
              <IconButton size="small" aria-label="View Region" color="primary">
                <InfoOutlined />
              </IconButton>
            </Tooltip>
          </Link>
          <Link to={`/app/crm/region/${row._id}/edit`} state={{ from: "list" }}>
            <Tooltip title={"Edit"} arrow>
              <IconButton size="small" aria-label="Edit Region">
                {/* <ModeEditOutlineTwoTone /> */}
              </IconButton>
            </Tooltip>
          </Link>
          {false && (
            <Tooltip
              title={row.status === "Active" ? "Deactivate" : "Activate"}
              arrow
            >
              <Switch
                checked={row.status === "Active"}
                onChange={() => handleStatusChange(row)}
                color="primary"
                size="small"
              />
            </Tooltip>
          )}
        </>
      ),
    },
  ];
  React.useEffect(() => {
    fetchRegions({
      status: activeTab,
      page: currentPage,
      limit: perPage,
      ...(keyword && { search: keyword }),
    });
  }, [activeTab, fetchRegions, currentPage, keyword, perPage]);

  useAppMeta(`${activeTab} Province`);

  return (
    <BreadcrumbLayout
      breadcrumbs={breadcrumbs}
      headerActions={isSystemAdmin && <PageActions />}
    >
      <BreadcrumbLayout.Error error={error} />
      <BreadcrumbLayout.Paper>
        <CustomTabs tabs={regionListTabs} activeTab={activeTab} />

        <>
          <TableFilter
            searchLabel="Name "
            disabled={loadingRegions || fetchingRegions}
          >
            <div className="font-medium">{activeTab} Province</div>
          </TableFilter>
          <AppTable
            columns={columns}
            data={regionRes?.data ?? []}
            progressPending={loadingRegions || fetchingRegions}
            paginationServer
            paginationPerPage={perPage}
            paginationTotalRows={regionRes?.totalItems ?? 0}
            paginationDefaultPage={currentPage}
            onChangePage={handlePageChange}
            onChangeRowsPerPage={handlePerRowsChange}
          />
        </>
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

const PageActions = () => {
  return (
    <div className="flex space-x-4">
      <Link to="/app/crm/region/create">
        <Button variant="contained" startIcon={<Add />}>
          New Province
        </Button>
      </Link>
    </div>
  );
};

export default RegionListScreen;
